import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  FilePdf, FileDoc, FileXls, FileAudio, FileVideo, FileImage,
  Check, Plus, Trash, Eye, EyeSlash, CaretDown, CaretUp,
  Lightning, ClipboardText, Scales, CheckSquare, Star, ChartBar,
  Upload, TextAa, ListChecks, Exam, ShieldCheck, UserCircle,
  FileMagnifyingGlass, Sparkle, Target, GraduationCap,
  Play, CircleNotch, FloppyDisk, BookmarkSimple, X,
  ToggleLeft, ToggleRight, Info,
} from '@phosphor-icons/react';

/* ─────────────────────────────────────────────────
   CONSTANTS
   ───────────────────────────────────────────────── */
const FILE_TYPES = [
  { id: 'audio', label: 'Audio', icon: FileAudio, exts: '.mp3, .wav, .m4a' },
  { id: 'video', label: 'Video', icon: FileVideo, exts: '.mp4, .mov, .webm' },
  { id: 'pdf',   label: 'PDF',   icon: FilePdf,   exts: '.pdf' },
  { id: 'doc',   label: 'Documents', icon: FileDoc, exts: '.docx, .doc, .txt' },
  { id: 'image', label: 'Images', icon: FileImage, exts: '.jpg, .png, .webp' },
  { id: 'sheet', label: 'Spreadsheets', icon: FileXls, exts: '.xlsx, .csv' },
];

const EVALUATION_MODES = [
  { id: 'insights',       label: 'Insights only',         icon: Sparkle,       desc: 'Analyse and summarise without scoring' },
  { id: 'score',          label: 'Score',                  icon: ChartBar,      desc: 'Assign a numeric score' },
  { id: 'pass_fail',      label: 'Pass / Fail',            icon: CheckSquare,   desc: 'Binary pass or fail decision' },
  { id: 'grade',          label: 'Grade / Rating',         icon: Star,          desc: 'Letter grade or star rating' },
  { id: 'criteria_based', label: 'Criteria-based scoring', icon: ListChecks,    desc: 'Score against individual criteria' },
];

const OUTPUT_FORMATS = [
  'Summary + Insights',
  'Full Report',
  'Data Extraction',
  'Checklist Review',
  'Scorecard',
  'Feedback Report',
  'Custom',
];

const EXPORT_FORMATS = [
  { id: 'pdf',  label: 'PDF' },
  { id: 'word', label: 'Word' },
  { id: 'text', label: 'Text' },
  { id: 'json', label: 'JSON' },
  { id: 'csv',  label: 'CSV' },
];

const PRESETS = [
  {
    id: 'general', label: 'General Review', icon: FileMagnifyingGlass,
    config: { evaluation_mode: 'insights', output_format: 'Summary + Insights', include_evidence: false, task_instructions: 'Review and summarise this file. Highlight key findings, patterns, and anything noteworthy.' },
  },
  {
    id: 'assignment', label: 'Assignment Marking', icon: Exam,
    config: { evaluation_mode: 'criteria_based', output_format: 'Scorecard', include_evidence: true, task_instructions: 'Mark this assignment against the provided rubric. Score each criterion and provide constructive feedback.', scoring_schema: [{ name: 'Understanding', weight: 25, notes: '' }, { name: 'Analysis', weight: 25, notes: '' }, { name: 'Presentation', weight: 25, notes: '' }, { name: 'Originality', weight: 25, notes: '' }] },
  },
  {
    id: 'assignment_marker', label: 'Assignment Marker (with Rubric)', icon: GraduationCap,
    config: {
      evaluation_mode: 'criteria_based',
      output_format: 'Scorecard',
      include_evidence: true,
      export_format: 'pdf',
      task_instructions: 'Mark this student submission using the provided rubric.\n\nFor each criterion:\n- Assign a score\n- Explain the reasoning\n- Reference relevant parts of the submission\n\nThen provide:\n- Total score and grade\n- Strengths\n- Areas for improvement\n- Constructive feedback suitable for the student\'s level\n\nIf no rubric is provided, evaluate using general academic standards.',
      scoring_schema: [
        { name: 'Content Understanding', weight: 40, notes: 'Depth of knowledge, accuracy, and critical thinking' },
        { name: 'Structure & Clarity', weight: 25, notes: 'Logical flow, clear arguments, and organisation' },
        { name: 'Grammar & Language', weight: 20, notes: 'Writing quality, spelling, and academic tone' },
        { name: 'Referencing / Evidence', weight: 15, notes: 'Use of sources, citations, and supporting evidence' },
      ],
    },
  },
  {
    id: 'qa_review', label: 'QA Review', icon: ClipboardText,
    config: { evaluation_mode: 'score', output_format: 'Checklist Review', include_evidence: true, task_instructions: 'Review this call or interaction against the quality checklist. Score overall quality and flag any issues.' },
  },
  {
    id: 'compliance', label: 'Compliance Check', icon: ShieldCheck,
    config: { evaluation_mode: 'pass_fail', output_format: 'Checklist Review', include_evidence: true, task_instructions: 'Check this document for compliance against the provided policy or standards. Flag any violations or gaps.' },
  },
  {
    id: 'resume', label: 'Resume Screening', icon: UserCircle,
    config: { evaluation_mode: 'grade', output_format: 'Feedback Report', include_evidence: false, task_instructions: 'Screen this resume against the job requirements. Rate fit and highlight strengths and gaps.' },
  },
  {
    id: 'proposal', label: 'Proposal Review', icon: Scales,
    config: { evaluation_mode: 'criteria_based', output_format: 'Scorecard', include_evidence: true, task_instructions: 'Evaluate this proposal against the requirements and scoring criteria.', scoring_schema: [{ name: 'Technical approach', weight: 30, notes: '' }, { name: 'Team & experience', weight: 25, notes: '' }, { name: 'Value for money', weight: 25, notes: '' }, { name: 'Delivery plan', weight: 20, notes: '' }] },
  },
  {
    id: 'extraction', label: 'Data Extraction', icon: Target,
    config: { evaluation_mode: 'insights', output_format: 'Data Extraction', include_evidence: false, task_instructions: 'Extract all structured data, key fields, and important values from this file. Present in a clean, organized format.' },
  },
];

const SAMPLE_TEST_TYPES = [
  { id: 'assignment', label: 'Student submission', desc: 'Marketing strategy assignment' },
  { id: 'call_transcript', label: 'Support call', desc: '8-min customer service transcript' },
  { id: 'simple_doc', label: 'Business report', desc: 'Q4 quarterly report' },
];

const OUTPUT_PREVIEWS = {
  'insights': {
    title: 'Insights Output',
    sections: ['Summary', 'Key Findings', 'Patterns & Trends', 'Recommendations'],
  },
  'score': {
    title: 'Scored Output',
    sections: ['Overall Score (0-100)', 'Score Breakdown by Area', 'Justification', 'Recommendations'],
  },
  'pass_fail': {
    title: 'Pass / Fail Output',
    sections: ['Decision: PASS or FAIL', 'Criteria Met', 'Criteria Failed', 'Notes'],
  },
  'grade': {
    title: 'Graded Output',
    sections: ['Grade (A-F or 1-5 Stars)', 'Grade Justification', 'Strengths', 'Areas for Improvement'],
  },
  'criteria_based': {
    title: 'Criteria Scorecard',
    sections: ['Per-Criterion Scores', 'Evidence per Criterion', 'Weighted Total', 'Summary Feedback'],
  },
};


/* ─────────────────────────────────────────────────
   SECTION WRAPPER
   ───────────────────────────────────────────────── */
const Section = ({ title, helper, children, testId }) => (
  <div className="mb-5" data-testid={testId}>
    <p className="text-[11px] font-bold text-[#7D8896] uppercase tracking-wider mb-1.5">{title}</p>
    {helper && <p className="text-[10px] text-[#5C6778] mb-3 leading-relaxed">{helper}</p>}
    {children}
  </div>
);

/* ─────────────────────────────────────────────────
   PILL BUTTON
   ───────────────────────────────────────────────── */
const Pill = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
      active
        ? 'bg-[rgba(0,229,192,0.1)] text-[#00E5C0] border border-[rgba(0,229,192,0.2)]'
        : 'bg-[rgba(255,255,255,0.02)] text-[#7D8896] border border-[rgba(255,255,255,0.08)] hover:text-[#B0B8C4] hover:border-[rgba(255,255,255,0.12)]'
    }`}
  >
    {children}
  </button>
);


/* ─────────────────────────────────────────────────
   MAIN COMPONENT
   ───────────────────────────────────────────────── */
const FileWorkerConfig = ({ config, onChange, agentId }) => {
  const [showCriteria, setShowCriteria] = useState(false);
  const [sampleTesting, setSampleTesting] = useState(false);
  const [sampleResult, setSampleResult] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showTemplateList, setShowTemplateList] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;

  // Load saved templates
  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const res = await fetch(`${API}/api/flow/templates`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSavedTemplates(data.templates || []);
      }
    } catch { /* ignore */ }
    setLoadingTemplates(false);
  }, [API]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // Run sample file test
  const runSampleTest = async (sampleType) => {
    if (!agentId) { toast.error('Save the worker first to test'); return; }
    setSampleTesting(true);
    setSampleResult(null);
    try {
      const res = await fetch(`${API}/api/flow/test-sample`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ agent_id: agentId, sample_type: sampleType }),
      });
      if (res.ok) {
        const data = await res.json();
        setSampleResult(data);
        toast.success('Sample test complete');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Test failed');
      }
    } catch {
      toast.error('Test failed - check your configuration');
    }
    setSampleTesting(false);
  };

  // Save as template
  const saveAsTemplate = async () => {
    if (!templateName.trim()) return;
    setSavingTemplate(true);
    try {
      const res = await fetch(`${API}/api/flow/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: templateName, config: c }),
      });
      if (res.ok) {
        toast.success('Template saved');
        setShowSaveDialog(false);
        setTemplateName('');
        fetchTemplates();
      } else {
        toast.error('Failed to save template');
      }
    } catch {
      toast.error('Failed to save template');
    }
    setSavingTemplate(false);
  };

  // Delete template
  const deleteTemplate = async (tplId) => {
    try {
      await fetch(`${API}/api/flow/templates/${tplId}`, { method: 'DELETE', credentials: 'include' });
      fetchTemplates();
      toast.success('Template removed');
    } catch { /* ignore */ }
  };

  // Load template
  const loadTemplate = (tpl) => {
    update({ ...tpl.config, acceptedTypes: c.acceptedTypes });
    setShowTemplateList(false);
    toast.success(`Loaded "${tpl.name}"`);
  };

  // Normalize config with backward compat
  const c = {
    acceptedTypes: config?.acceptedTypes || [],
    task_instructions: config?.task_instructions || config?.instructions || '',
    reference_material_text: config?.reference_material_text || '',
    reference_file_ids: config?.reference_file_ids || [],
    ref_input_type: config?.ref_input_type || 'text',
    ref_required: config?.ref_required ?? false,
    evaluation_mode: config?.evaluation_mode || 'insights',
    scoring_schema: config?.scoring_schema || [],
    include_evidence: config?.include_evidence ?? false,
    output_format: config?.output_format || config?.outputFormat || 'Summary + Insights',
    export_format: config?.export_format || 'pdf',
  };

  const update = (patch) => {
    onChange({
      ...config,
      ...patch,
      // backward compat sync
      instructions: patch.task_instructions ?? c.task_instructions,
      outputFormat: patch.output_format ?? c.output_format,
    });
  };

  const toggleFileType = (id) => {
    const next = c.acceptedTypes.includes(id)
      ? c.acceptedTypes.filter(t => t !== id)
      : [...c.acceptedTypes, id];
    update({ acceptedTypes: next });
  };

  const updateCriterion = (idx, field, value) => {
    const next = [...c.scoring_schema];
    next[idx] = { ...next[idx], [field]: value };
    update({ scoring_schema: next });
  };

  const addCriterion = () => {
    update({ scoring_schema: [...c.scoring_schema, { name: '', weight: 0, notes: '' }] });
  };

  const removeCriterion = (idx) => {
    update({ scoring_schema: c.scoring_schema.filter((_, i) => i !== idx) });
  };

  const applyPreset = (preset) => {
    update({
      ...preset.config,
      // keep file types untouched
      acceptedTypes: c.acceptedTypes,
    });
  };

  const hasReference = c.ref_input_type !== 'text' || c.ref_required;

  return (
    <div className="space-y-0" data-testid="file-worker-config">

      {/* ── PRESETS BAR ── */}
      <div className="mb-5" data-testid="presets-bar">
        <p className="text-[10px] font-semibold text-[#5C6778] uppercase tracking-wider mb-2">Quick start</p>
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map(p => {
            const Icon = p.icon;
            return (
              <button
                key={p.id}
                onClick={() => applyPreset(p)}
                data-testid={`preset-${p.id}`}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-[#7D8896] bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.06)] hover:text-[#00E5C0] hover:border-[rgba(0,229,192,0.15)] hover:bg-[rgba(0,229,192,0.03)] transition-all"
              >
                <Icon size={12} weight="duotone" />
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── 1. FILES TO ANALYSE ── */}
      <Section title="Files to analyse" testId="section-file-types">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {FILE_TYPES.map(ft => {
            const Icon = ft.icon;
            const active = c.acceptedTypes.includes(ft.id);
            return (
              <button key={ft.id} onClick={() => toggleFileType(ft.id)} data-testid={`file-type-${ft.id}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-left ${
                  active ? 'border-[rgba(0,229,192,0.3)] bg-[rgba(0,229,192,0.04)]' : 'border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.12)]'
                }`}>
                <Icon size={20} className={active ? 'text-[#00E5C0]' : 'text-[#7D8896]'} weight="duotone" />
                <div>
                  <p className={`text-sm font-medium ${active ? 'text-[#F0F4F8]' : 'text-[#B0B8C4]'}`}>{ft.label}</p>
                  <p className="text-[10px] text-[#5C6778]">{ft.exts}</p>
                </div>
                {active && <Check size={14} className="ml-auto text-[#00E5C0]" weight="bold" />}
              </button>
            );
          })}
        </div>
      </Section>

      {/* ── 2. TASK INSTRUCTIONS ── */}
      <Section
        title="Task instructions"
        helper="Tell the worker what to do with the uploaded file."
        testId="section-task-instructions"
      >
        <textarea
          value={c.task_instructions}
          onChange={(e) => update({ task_instructions: e.target.value })}
          rows={3}
          data-testid="file-task-instructions"
          placeholder="e.g. Review this customer service call for quality. Score tone, resolution, and compliance. Provide insights and recommendations."
          className="w-full px-4 py-3 bg-[#1A2332] border border-[rgba(255,255,255,0.08)] rounded-xl text-sm text-[#F0F4F8] placeholder-[#5C6778] focus:outline-none focus:border-[rgba(0,229,192,0.4)] transition-colors resize-none"
        />
      </Section>

      {/* ── 3. REFERENCE MATERIAL CONFIG ── */}
      <Section
        title="Reference material or criteria"
        helper="Configure how reference material (rubrics, checklists, policies) is provided at runtime."
        testId="section-reference-material"
      >
        <div className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[#1A2332] p-3.5 space-y-4">
          {/* Input type selector */}
          <div>
            <p className="text-[10px] font-semibold text-[#7D8896] uppercase tracking-wider mb-2">Accepted input type</p>
            <div className="grid grid-cols-3 gap-1.5">
              {[
                { id: 'text', label: 'Text only', icon: TextAa },
                { id: 'file', label: 'File only', icon: Upload },
                { id: 'text_and_file', label: 'Text + File', icon: ListChecks },
              ].map(opt => {
                const Icon = opt.icon;
                const active = (c.ref_input_type || 'text') === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => update({ ref_input_type: opt.id })}
                    data-testid={`ref-input-type-${opt.id}`}
                    className={`flex flex-col items-center gap-1.5 px-2 py-2.5 rounded-lg border text-center transition-all ${
                      active
                        ? 'border-[rgba(0,229,192,0.3)] bg-[rgba(0,229,192,0.04)] text-[#00E5C0]'
                        : 'border-[rgba(255,255,255,0.06)] text-[#7D8896] hover:text-[#B0B8C4] hover:border-[rgba(255,255,255,0.1)]'
                    }`}
                  >
                    <Icon size={16} weight="duotone" />
                    <span className="text-[10px] font-medium leading-tight">{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Required / Optional toggle */}
          <div className="flex items-center justify-between px-1">
            <div>
              <p className="text-[10px] font-semibold text-[#7D8896] uppercase tracking-wider">Requirement</p>
              <p className="text-[10px] text-[#5C6778] mt-0.5">Must the user provide reference material?</p>
            </div>
            <button
              onClick={() => update({ ref_required: !c.ref_required })}
              data-testid="ref-required-toggle"
              className="flex items-center gap-2"
            >
              {c.ref_required ? (
                <ToggleRight size={28} weight="fill" className="text-[#00E5C0]" />
              ) : (
                <ToggleLeft size={28} weight="fill" className="text-[#5C6778]" />
              )}
              <span className={`text-[11px] font-medium ${c.ref_required ? 'text-[#00E5C0]' : 'text-[#7D8896]'}`}>
                {c.ref_required ? 'Required' : 'Optional'}
              </span>
            </button>
          </div>

          {/* Contextual info */}
          <div className="flex items-start gap-2 px-2.5 py-2 rounded-lg bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.04)]">
            <Info size={13} className="text-[#5C6778] shrink-0 mt-0.5" weight="duotone" />
            <p className="text-[10px] text-[#5C6778] leading-relaxed">
              {(c.ref_input_type || 'text') === 'text' && 'Users will paste text criteria (rubric, checklist, policy) when running this worker.'}
              {(c.ref_input_type || 'text') === 'file' && 'Users will upload a reference file (PDF, DOCX) when running this worker.'}
              {(c.ref_input_type || 'text') === 'text_and_file' && 'Users can paste text and/or upload reference files when running this worker.'}
              {c.ref_required ? ' This is a required field.' : ' This is optional — general standards will be used if nothing is provided.'}
            </p>
          </div>
        </div>
      </Section>

      {/* ── 4. EVALUATION MODE ── */}
      <Section
        title="Evaluation mode"
        helper="How should the worker assess the uploaded file?"
        testId="section-evaluation-mode"
      >
        <div className="grid grid-cols-1 gap-1.5">
          {EVALUATION_MODES.map(mode => {
            const Icon = mode.icon;
            const active = c.evaluation_mode === mode.id;
            return (
              <button
                key={mode.id}
                onClick={() => update({ evaluation_mode: mode.id })}
                data-testid={`eval-mode-${mode.id}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-left ${
                  active
                    ? 'border-[rgba(0,229,192,0.3)] bg-[rgba(0,229,192,0.04)]'
                    : 'border-[rgba(255,255,255,0.06)] hover:border-[rgba(255,255,255,0.1)] bg-transparent'
                }`}
              >
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${active ? 'bg-[rgba(0,229,192,0.1)]' : 'bg-[rgba(255,255,255,0.03)]'}`}>
                  <Icon size={15} weight="duotone" className={active ? 'text-[#00E5C0]' : 'text-[#7D8896]'} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium ${active ? 'text-[#F0F4F8]' : 'text-[#B0B8C4]'}`}>{mode.label}</p>
                  <p className="text-[10px] text-[#5C6778] truncate">{mode.desc}</p>
                </div>
                {active && <Check size={14} className="text-[#00E5C0] shrink-0" weight="bold" />}
              </button>
            );
          })}
        </div>

        {/* Criteria editor - only when criteria_based selected */}
        {c.evaluation_mode === 'criteria_based' && (
          <div className="mt-3 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[#1A2332] p-3" data-testid="criteria-editor">
            <div className="flex items-center justify-between mb-2.5">
              <p className="text-[10px] font-semibold text-[#7D8896] uppercase tracking-wider">Scoring criteria</p>
              <button
                onClick={() => setShowCriteria(!showCriteria)}
                className="text-[10px] text-[#5C6778] hover:text-[#B0B8C4] transition-colors flex items-center gap-1"
              >
                {showCriteria ? <CaretUp size={10} /> : <CaretDown size={10} />}
                {showCriteria ? 'Collapse' : 'Expand'}
              </button>
            </div>

            {(showCriteria || c.scoring_schema.length === 0) && (
              <>
                {c.scoring_schema.length > 0 ? (
                  <div className="space-y-2">
                    {/* Header row */}
                    <div className="grid grid-cols-[1fr_64px_1fr_28px] gap-2 px-1">
                      <span className="text-[9px] text-[#5C6778] uppercase tracking-wider">Criterion</span>
                      <span className="text-[9px] text-[#5C6778] uppercase tracking-wider">Weight</span>
                      <span className="text-[9px] text-[#5C6778] uppercase tracking-wider">Notes</span>
                      <span></span>
                    </div>
                    {c.scoring_schema.map((crit, idx) => (
                      <div key={idx} className="grid grid-cols-[1fr_64px_1fr_28px] gap-2 items-center" data-testid={`criterion-${idx}`}>
                        <input
                          value={crit.name}
                          onChange={(e) => updateCriterion(idx, 'name', e.target.value)}
                          placeholder="e.g. Clarity"
                          data-testid={`criterion-name-${idx}`}
                          className="px-2.5 py-2 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] rounded-lg text-xs text-[#F0F4F8] placeholder-[#5C6778] focus:outline-none focus:border-[rgba(0,229,192,0.3)]"
                        />
                        <input
                          type="number"
                          value={crit.weight || ''}
                          onChange={(e) => updateCriterion(idx, 'weight', parseInt(e.target.value) || 0)}
                          placeholder="0"
                          data-testid={`criterion-weight-${idx}`}
                          className="px-2.5 py-2 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] rounded-lg text-xs text-[#F0F4F8] placeholder-[#5C6778] focus:outline-none focus:border-[rgba(0,229,192,0.3)] text-center"
                        />
                        <input
                          value={crit.notes || ''}
                          onChange={(e) => updateCriterion(idx, 'notes', e.target.value)}
                          placeholder="Optional notes"
                          data-testid={`criterion-notes-${idx}`}
                          className="px-2.5 py-2 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] rounded-lg text-xs text-[#F0F4F8] placeholder-[#5C6778] focus:outline-none focus:border-[rgba(0,229,192,0.3)]"
                        />
                        <button onClick={() => removeCriterion(idx)} className="w-7 h-7 flex items-center justify-center rounded-lg text-[#5C6778] hover:text-[#FF6B6B] hover:bg-[rgba(255,107,107,0.06)] transition-colors" data-testid={`criterion-remove-${idx}`}>
                          <Trash size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[10px] text-[#5C6778] mb-2">No criteria added yet. Add criteria to score against.</p>
                )}
                <button
                  onClick={addCriterion}
                  data-testid="add-criterion-btn"
                  className="mt-2 flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-medium text-[#7D8896] border border-dashed border-[rgba(255,255,255,0.1)] hover:text-[#00E5C0] hover:border-[rgba(0,229,192,0.2)] transition-colors w-full justify-center"
                >
                  <Plus size={12} />
                  Add criterion
                </button>
              </>
            )}

            {showCriteria && c.scoring_schema.length > 0 && (() => {
              const total = c.scoring_schema.reduce((sum, cr) => sum + (cr.weight || 0), 0);
              return (
                <div className="mt-2 flex items-center justify-between px-1">
                  <span className="text-[10px] text-[#5C6778]">{c.scoring_schema.length} criteria</span>
                  <span className={`text-[10px] font-medium ${total === 100 ? 'text-[#00E5C0]' : 'text-[#F59E0B]'}`}>
                    Total weight: {total}{total !== 100 ? ' (suggest 100)' : ''}
                  </span>
                </div>
              );
            })()}
          </div>
        )}
      </Section>

      {/* ── 5. OUTPUT FORMAT ── */}
      <Section title="Output format" testId="section-output-format">
        <div className="flex flex-wrap gap-1.5">
          {OUTPUT_FORMATS.map(fmt => (
            <Pill
              key={fmt}
              active={c.output_format === fmt}
              onClick={() => update({ output_format: fmt })}
              testId={`output-format-${fmt.toLowerCase().replace(/[\s+/]+/g, '-')}`}
            >
              {fmt}
            </Pill>
          ))}
        </div>
      </Section>

      {/* ── 6. EVIDENCE TOGGLE ── */}
      <Section
        title="Evidence for decisions"
        helper="Include supporting excerpts, matched criteria, or reasons in the output."
        testId="section-evidence-toggle"
      >
        <button
          onClick={() => update({ include_evidence: !c.include_evidence })}
          data-testid="evidence-toggle"
          className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border transition-all text-left bg-[#1A2332] border-[rgba(255,255,255,0.08)]"
        >
          <div className={`w-9 h-5 rounded-full relative transition-colors ${c.include_evidence ? 'bg-[#00E5C0]' : 'bg-[#333]'}`}>
            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${c.include_evidence ? 'translate-x-4' : 'translate-x-0.5'}`} />
          </div>
          <div className="flex-1">
            <p className={`text-xs font-medium ${c.include_evidence ? 'text-[#F0F4F8]' : 'text-[#B0B8C4]'}`}>
              Show evidence for decisions
            </p>
          </div>
          {c.include_evidence ? <Eye size={14} className="text-[#00E5C0]" /> : <EyeSlash size={14} className="text-[#5C6778]" />}
        </button>
      </Section>

      {/* ── 7. EXPORT FORMAT ── */}
      <Section title="Export format" testId="section-export-format">
        <div className="flex gap-1.5">
          {EXPORT_FORMATS.map(ef => (
            <Pill
              key={ef.id}
              active={c.export_format === ef.id}
              onClick={() => update({ export_format: ef.id })}
              testId={`export-fmt-${ef.id}`}
            >
              {ef.label}
            </Pill>
          ))}
        </div>
      </Section>

      {/* ── OUTPUT PREVIEW ── */}
      <Section title="Expected output structure" testId="section-output-preview">
        <div className="rounded-xl border border-[rgba(255,255,255,0.06)] bg-[#1A2332] p-3.5">
          <p className="text-[10px] font-semibold text-[#00E5C0] uppercase tracking-wider mb-2">
            {OUTPUT_PREVIEWS[c.evaluation_mode]?.title || 'Output Preview'}
          </p>
          <div className="space-y-1.5">
            {(OUTPUT_PREVIEWS[c.evaluation_mode]?.sections || []).map((sec, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-1 h-1 rounded-full bg-[rgba(0,229,192,0.4)]" />
                <span className="text-[11px] text-[#B0B8C4]">{sec}</span>
              </div>
            ))}
          </div>
          {c.include_evidence && (
            <div className="mt-2 pt-2 border-t border-[rgba(255,255,255,0.05)]">
              <div className="flex items-center gap-2">
                <div className="w-1 h-1 rounded-full bg-[rgba(245,158,11,0.5)]" />
                <span className="text-[11px] text-[#F59E0B]">+ Evidence & supporting excerpts</span>
              </div>
            </div>
          )}
          {hasReference && (
            <div className="mt-1">
              <div className="flex items-center gap-2">
                <div className="w-1 h-1 rounded-full bg-[rgba(245,158,11,0.5)]" />
                <span className="text-[11px] text-[#F59E0B]">+ Evaluated against reference material ({c.ref_required ? 'required' : 'if provided'})</span>
              </div>
            </div>
          )}
        </div>
      </Section>

      {/* ── RUN WITH SAMPLE FILE ── */}
      {agentId && (
        <Section title="Test with sample file" helper="Run this worker instantly against a built-in sample to validate your configuration." testId="section-sample-test">
          <div className="flex gap-1.5 flex-wrap">
            {SAMPLE_TEST_TYPES.map(st => (
              <button
                key={st.id}
                onClick={() => runSampleTest(st.id)}
                disabled={sampleTesting}
                data-testid={`sample-test-${st.id}`}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-medium text-[#B0B8C4] bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)] hover:text-[#00E5C0] hover:border-[rgba(0,229,192,0.2)] hover:bg-[rgba(0,229,192,0.03)] transition-all disabled:opacity-50"
              >
                {sampleTesting ? <CircleNotch size={12} className="animate-spin" /> : <Play size={12} weight="fill" />}
                {st.label}
              </button>
            ))}
          </div>

          {sampleResult && (
            <div className="mt-3 rounded-xl border border-[rgba(0,229,192,0.15)] bg-[rgba(0,229,192,0.02)] p-4" data-testid="sample-test-result">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-semibold text-[#00E5C0] uppercase tracking-wider">Test Result - {sampleResult.sample_file}</span>
                <button onClick={() => setSampleResult(null)} className="text-[#5C6778] hover:text-white"><X size={12} /></button>
              </div>
              <div className="text-xs text-[#F0F4F8] whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto scrollbar-hide">
                {sampleResult.output?.response || sampleResult.output?.text || sampleResult.output_payload?.markdown || sampleResult.output_payload?.text || sampleResult.summary || 'No output generated'}
              </div>
            </div>
          )}
        </Section>
      )}

      {/* ── SAVE AS TEMPLATE / LOAD TEMPLATE ── */}
      <Section title="Templates" helper="Save this configuration for reuse across workers." testId="section-templates">
        <div className="flex gap-2">
          <button
            onClick={() => setShowSaveDialog(true)}
            data-testid="save-template-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-medium text-[#B0B8C4] border border-[rgba(255,255,255,0.08)] hover:text-[#00E5C0] hover:border-[rgba(0,229,192,0.2)] transition-colors"
          >
            <FloppyDisk size={13} weight="duotone" />
            Save as template
          </button>
          {savedTemplates.length > 0 && (
            <button
              onClick={() => setShowTemplateList(!showTemplateList)}
              data-testid="load-template-btn"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-medium text-[#B0B8C4] border border-[rgba(255,255,255,0.08)] hover:text-[#00E5C0] hover:border-[rgba(0,229,192,0.2)] transition-colors"
            >
              <BookmarkSimple size={13} weight="duotone" />
              Load template ({savedTemplates.length})
            </button>
          )}
        </div>

        {/* Save dialog */}
        {showSaveDialog && (
          <div className="mt-2 flex gap-2 items-center" data-testid="save-template-dialog">
            <input
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Template name"
              autoFocus
              data-testid="template-name-input"
              className="flex-1 px-3 py-2 bg-[#1A2332] border border-[rgba(255,255,255,0.1)] rounded-lg text-xs text-[#F0F4F8] placeholder-[#5C6778] focus:outline-none focus:border-[rgba(0,229,192,0.3)]"
              onKeyDown={(e) => e.key === 'Enter' && saveAsTemplate()}
            />
            <button
              onClick={saveAsTemplate}
              disabled={savingTemplate || !templateName.trim()}
              className="px-3 py-2 rounded-lg text-xs font-medium bg-[rgba(0,229,192,0.1)] text-[#00E5C0] hover:bg-[rgba(0,229,192,0.15)] disabled:opacity-50 transition-colors"
            >
              {savingTemplate ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => { setShowSaveDialog(false); setTemplateName(''); }} className="text-[#5C6778] hover:text-white p-1">
              <X size={14} />
            </button>
          </div>
        )}

        {/* Template list */}
        {showTemplateList && (
          <div className="mt-2 rounded-xl border border-[rgba(255,255,255,0.06)] bg-[#1A2332] overflow-hidden" data-testid="template-list">
            {loadingTemplates ? (
              <div className="p-4 text-center"><CircleNotch size={16} className="animate-spin mx-auto text-[#5C6778]" /></div>
            ) : savedTemplates.length === 0 ? (
              <div className="p-4 text-center text-[10px] text-[#5C6778]">No saved templates yet</div>
            ) : (
              savedTemplates.map(tpl => (
                <div key={tpl.template_id} className="flex items-center gap-3 px-3 py-2.5 border-b border-[rgba(255,255,255,0.04)] last:border-0 hover:bg-[rgba(255,255,255,0.02)] transition-colors">
                  <BookmarkSimple size={13} className="text-[#7D8896] shrink-0" weight="duotone" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[#F0F4F8] font-medium truncate">{tpl.name}</p>
                    {tpl.description && <p className="text-[10px] text-[#5C6778] truncate">{tpl.description}</p>}
                  </div>
                  <button
                    onClick={() => loadTemplate(tpl)}
                    className="shrink-0 px-2.5 py-1 rounded-md text-[10px] font-medium text-[#00E5C0] bg-[rgba(0,229,192,0.06)] hover:bg-[rgba(0,229,192,0.12)] transition-colors"
                  >
                    Load
                  </button>
                  <button
                    onClick={() => deleteTemplate(tpl.template_id)}
                    className="shrink-0 text-[#5C6778] hover:text-[#FF6B6B] transition-colors"
                  >
                    <Trash size={12} />
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </Section>
    </div>
  );
};

export default FileWorkerConfig;
